import { Component, computed, DestroyRef, inject, LOCALE_ID, signal } from '@angular/core';
import { OrderSummaryCard } from '../../view-order-details/utils/order-summary-card/order-summary-card';
import { ErrorData } from '../../../../../utils/error-data/error-data';
import { ViewOrderModel } from '../../../../../pages/admin-dashboard/orders-management/view-order/view-order.model';
import { resetPackageActions } from '../../../../../store/order/confirm-package-order/confirm-package.actions';
import { finalize, forkJoin } from 'rxjs';
import { Store } from '@ngrx/store';
import { OrderService } from '../../../../../services/orders/order.service';
import { hydratePackageItems } from '../../../../../store/order/package-order/package-item.actions';
import { SafeUrl } from '@angular/platform-browser';
import { ProductsFileMgt } from '../../../../../services/product/file-management/products.file-mgt.service';
import { ActivatedRoute } from '@angular/router';
import { InvoiceService } from '../../../../../services/invoices/invoices.service';
import { InvoicesResponse } from '../../../../../pages/admin-dashboard/admin-invoice/admin-invoice.model';
import { InvoiceBlock } from './utils/invoice-block/invoice-block';
import { Loading } from '../../../../../utils/loading/loading';

@Component({
  selector: 'app-view-order-related-invoices',
  imports: [OrderSummaryCard, ErrorData, InvoiceBlock, Loading],
  templateUrl: './view-order-related-invoices.html',
  styleUrl: './view-order-related-invoices.scss',
})
export class ViewOrderRelatedInvoices {
  private route = inject(ActivatedRoute);
  private localeId = inject(LOCALE_ID);
  isArabic = computed(() => this.localeId.startsWith('ar'));
  visibleItems = signal<InvoicesResponse['list']>([]);
  orderData = signal<ViewOrderModel | null>(null);
  loading = signal(false);
  error = signal('');
  private store = inject(Store);
  private orderService = inject(OrderService);
  private invoiceService = inject(InvoiceService);
  imagePreviews = signal<Record<string, string | SafeUrl>>({});
  private fileMgt = inject(ProductsFileMgt);
  private destroyRef = inject(DestroyRef);
  orderId = signal('');

  private preloadImages(data: ViewOrderModel) {
    const fids = new Set<string>();
    for (const item of data.items) {
      if ((item as any).product?.profileImageFid) fids.add((item as any).product.profileImageFid);
      if ((item as any).offer?.profileImageFids?.length)
        (item as any).offer.profileImageFids.forEach((f: any) => fids.add(f));
      if ((item as any).bundle?.profileImageFid) fids.add((item as any).bundle.profileImageFid);
      if ((item as any).bundle?.imagesFids?.length) (item as any).bundle.imagesFids.forEach((f: any) => fids.add(f));
    }
    fids.forEach((fid) => this.resolveImage(fid));
  }

  resolveImage(fid: string) {
    if (!fid || this.imagePreviews()[fid]) return;
    this.fileMgt.getImageUrl(fid).subscribe({
      next: (res) => {
        this.imagePreviews.update((map) => ({
          ...map,
          [fid]: res.isImage ? res.url : '/assets/product.png',
        }));
      },
      error: () => {
        this.imagePreviews.update((map) => ({
          ...map,
          [fid]: '/assets/product.png',
        }));
      },
    });
  }

  loadOrderData(id: string) {
    this.loading.set(true);
    this.error.set('');
    this.store.dispatch(resetPackageActions());

    const subscription = forkJoin({
      items: this.orderService.getOrderById(id),
      summary: this.orderService.getOrderSummaryById(id),
    })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: ({ items, summary }) => {
          const merged = { ...items, ...summary };
          this.orderData.set(merged);
          this.store.dispatch(hydratePackageItems({ data: merged }));
          this.preloadImages(merged);
        },
        error: (err) => {
          this.error.set(
            this.isArabic() ? err.error?.messageAr || 'حدث بعض الخطأ' : err.error?.messageEn || 'some error occured!',
          );
        },
      });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  totalPeoplePrice = computed(() => {
    const items = this.visibleItems();

    return items.reduce((total: number, item: any) => {
      // product
      if (item.product) {
        return total + Number(item.product?.peoplePrice ?? 0) * Number(item.quantity ?? 0);
      }

      // Offer / Bundle
      const products = item.products ?? item.offer?.products ?? item.bxGyOffer?.products ?? item.bundle?.products ?? [];

      const itemTotal = products.reduce((sum: number, product: any) => {
        return sum + Number(product?.product?.peoplePrice ?? 0) * Number(product?.quantity ?? 0);
      }, 0);

      return total + itemTotal;
    }, 0);
  });

  ngOnInit() {
    const subscription = this.route.paramMap.subscribe((params) => {
      const id = params.get('id');

      if (id) {
        this.orderId.set(id);
        this.store.dispatch(resetPackageActions());
        this.loadOrderData(id);
        this.getOrderInvoices(id);
      }
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  getOrderInvoices(orderId: string) {
    const subscription = this.invoiceService.getInvoicesByFilters(undefined, Number(orderId)).subscribe({
      next: (res: InvoicesResponse) => {
        this.visibleItems.set(res.list);
      },
      error: (err) => {
        this.error.set(
          this.isArabic() ? err.error?.messageAr || 'حدث بعض الخطأ' : err.error?.messageEn || 'some error occured!',
        );
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
}
